import React, { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPostsPaginated } from "../lib/sanity/client.js";
import { Link } from "react-router-dom";
import { urlFor } from "../lib/sanity/image.js";
import "./BlogPreview.css";

const BlogPreview = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["latest-posts"],
    queryFn: () => getPostsPaginated(1, 10), // fetch more
    staleTime: 5 * 60 * 1000,
  });

  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const cardWidth =
        scrollRef.current.querySelector(".blog-preview__card")?.offsetWidth ||
        350;
      const gap = 20;
      const scrollAmount = cardWidth + gap;

      if (direction === "left") {
        scrollRef.current.scrollBy({ left: -scrollAmount, behavior: "smooth" });
      } else {
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    }
  };

  if (isLoading) {
    return (
      <section className="blog-preview">
        <div className="blog-preview__container">
          <h2 className="blog-preview__title">Latest from Our Blog</h2>
          <p className="blog-preview__subtitle">
            Stay informed with expert insights and financial tips
          </p>
          <p>Loading blogs...</p>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="blog-preview">
        <div className="blog-preview__container">
          <h2 className="blog-preview__title">Failed to load blogs</h2>
        </div>
      </section>
    );
  }

  const { posts } = data;

  return (
    <section className="blog-preview">
      <div className="blog-preview__container">
        <div className="blog-preview__header">
          <h2 className="blog-preview__title">Latest from Our Blog</h2>
          <p className="blog-preview__subtitle">
            Stay informed with expert insights and financial tips
          </p>
        </div>

        <div className="blog-preview__carousel">
          <button
            className="blog-preview__nav blog-preview__nav--left"
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            ‹
          </button>

          <div className="blog-preview__scroll" ref={scrollRef}>
            {posts.map((post) => (
              <article key={post._id} className="blog-preview__card">
                <div className="blog-preview__image-container">
                  {post?.mainImage && (
                    <img
                      src={urlFor(post.mainImage)
                        .width(500)
                        .height(300)
                        .quality(80)
                        .auto("format")
                        .url()}
                      alt={post.title}
                      className="blog-preview__image"
                    />
                  )}
                  {post.categories?.length > 0 && (
                    <div className="blog-preview__category-badge">
                      {post.categories[0].title}
                    </div>
                  )}
                </div>
                <div className="blog-preview__content">
                  <h3 className="blog-preview__post-title">{post.title}</h3>
                  <Link
                    to={`/blogs/${post?.slug?.current}`}
                    className="blog-preview__read-more"
                  >
                    Read More →
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <button
            className="blog-preview__nav blog-preview__nav--right"
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            ›
          </button>
        </div>

        <div className="blog-preview__footer">
          <Link to="/blogs" className="blog-preview__view-all">
            View All Articles →
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogPreview;
